/**
 * **Targeting mode** — `projects/token-command-menu.md` 2.5, v18 of the prototype's
 * `project/Main.dc.html`. While the board-wide aim is armed on a unit-target action, the combat
 * canvas edge glows in the action's colour and a bottom-centre banner says what to click, how far
 * it reaches and how many units it can reach, with `CANCEL · ESC`.
 *
 * Keyed on the armed aim alone, so any surface that arms one (the command menu's rows, its fire card
 * per shot) gets the same mode. It resolves nothing: the board click still goes through
 * `CombatApp`'s `fireAt`, which clears the aim and so ends the mode. It only cancels: the CANCEL
 * button, and Esc through the layer-3 `armed-action` entry. A stationary right-click on the board
 * cancels too, but the board owns it (`CombatStage`), for every layer-3 member, not only an aim
 * with a banner.
 *
 * A surface may fill the unit column's targeting **slot** (`.tcm-tgt-slot`, `UnitColumn.tsx`, above
 * the unit panel) with its own targeting panel: the command menu host portals Ram's knock-back
 * toggle and a directed deploy's back-one-pick there, clear of the board's centre, the banner
 * (bottom centre) and the hover forecast (top-left). A filled slot carries its own exit, so the
 * banner drops its CANCEL (`token-command-menu.css`; Esc and a right-click on the board still disarm). The fire sequence uses no slot: its weapons and the shot's controls stay on
 * the command menu's fire card, and the menu does not stow for it. While that card is the menu's
 * active screen its own CANCEL is the exit, so the host passes `cancel={false}`.
 */
import { faCrosshairs } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import type { CSSProperties } from "react";
import type { Combatant, EncounterState } from "@lancer-vtt/domain";
import { techMenuPick } from "@lancer-vtt/rules";
import type { ActionDraft } from "./combatInspector";
import { weaponProfileForAim, weaponRangeForAim } from "./attackRange";
import { formatWeaponRange } from "./weaponFormat";
import { TARGETING_RGB, type TargetingKind } from "../../routes/home/pixiCombatBoard";
import "./token-command-menu.css";

/**
 * What the banner says. `null` for an aim that picks nothing on the board. `step` is a menu-armed
 * Full Tech's "1 of 2" (or a directed deploy's pick count), rendered as its own chip so an
 * ellipsized title never cuts it.
 */
export type TargetingBanner = { kind: TargetingKind; title: string; step?: string; sub: string };

const TECH_VERB: Readonly<Record<string, string>> = {
  scan: "Select target to scan",
  lock_on: "Select target to lock on",
  invade: "Select target to invade",
  bolster: "Select an ally to bolster",
};

function titleFor(attacker: Combatant, aim: ActionDraft): { title: string; step?: string } | null {
  const title = titleTextFor(attacker, aim);
  if (!title) return null;
  // A menu-armed Full Tech says which of its two picks this is.
  if (aim.mode === "full_tech" && aim.queue) return { title, step: `${aim.queue.length > 0 ? 1 : 2} of 2` };
  if (aim.mode === "activate_sites") return { title, step: `${Math.min(aim.picks.length + 1, aim.count)} of ${aim.count}` };
  return { title };
}

/**
 * The cell picks (3.2): a pilot's landing space (Dismount, Eject) or a deploy/throw site. Their
 * banner names the verb and its reach in place of the attack kind, and counts legal spaces.
 * Exported as copy data for `rulesCopyRegistry.ts`: Dismount's adjacent space and Eject's fly 6
 * restate Core p.70.
 */
export const CELL_PICK_COPY = {
  dismount: { title: "Select a space to dismount into", verb: "DISMOUNT", reach: "ADJACENT SPACE" },
  eject: { title: "Select a space to eject to", verb: "EJECT", reach: "FLY 6" },
  activate_cell: { title: "Select a target space", verb: "DEPLOY", reach: "" },
  activate_sites: { title: "Select a space to deploy", verb: "DEPLOY", reach: "" },
} as const;

type CellPickMode = keyof typeof CELL_PICK_COPY;
const isCellPick = (aim: ActionDraft): aim is Extract<ActionDraft, { mode: CellPickMode }> => aim.mode in CELL_PICK_COPY;

/** The legal-space count line: `N SPACES`, or `NO LEGAL SPACE` (the server's own toast says why). */
export function cellCountLine(n: number): string {
  return n === 0 ? "NO LEGAL SPACE" : `${n} ${n === 1 ? "SPACE" : "SPACES"}`;
}

function titleTextFor(attacker: Combatant, aim: ActionDraft): string | null {
  switch (aim.mode) {
    case "skirmish":
    case "barrage": {
      // A Barrage between shots keeps its aim with no weapon: there is nothing to target yet.
      if (aim.mode === "barrage" && aim.weaponId === "") return null;
      const weapon = weaponProfileForAim(attacker, aim);
      return weapon ? `Select target for ${weapon.name}` : "Select a target";
    }
    case "ram":
      return "Select target to ram";
    case "grapple":
      return "Select target to grapple";
    case "improvised_attack":
      return "Select target for improvised attack";
    case "quick_tech":
      return aim.optionId ? (TECH_VERB[techMenuPick(aim.optionId).option] ?? "Select a target") : null;
    case "full_tech": {
      if (!aim.optionId) return null;
      return TECH_VERB[techMenuPick(aim.optionId).option] ?? "Select a target for Full Tech";
    }
    case "tech_attack":
    case "activate":
      return "Select a target";
    case "dismount":
    case "eject":
    case "activate_cell":
    case "activate_sites":
      return CELL_PICK_COPY[aim.mode].title;
    default:
      return null;
  }
}

function reachFor(args: { state: EncounterState; attacker: Combatant; aim: ActionDraft; kind: TargetingKind }): string {
  const { state, attacker, aim, kind } = args;
  if (kind === "tech") return attacker.base.sensors !== undefined ? `SENSORS ${attacker.base.sensors}` : "SENSORS";
  if (aim.mode === "ram" || aim.mode === "grapple" || aim.mode === "improvised_attack") return "ADJACENT";
  const range = weaponRangeForAim(attacker, aim, state.activation.turnOrdinal);
  return range ? formatWeaponRange(range).toUpperCase() : "RANGE";
}

/**
 * The banner for an armed `aim`, or `null` when it picks nothing on the board. `legalCount` is the
 * size of the washes' own legal-target set (`useTargetingWashes`' `legalTargetIds`), never
 * recounted; `legalCells`, for a cell pick, is the size of its legal-space wash (`attackRange`),
 * absent when the pick is ungated.
 */
export function targetingBanner(args: { state: EncounterState; aim: ActionDraft | null; legalCount: number; legalCells?: number }): TargetingBanner | null {
  const { state, aim, legalCount, legalCells } = args;
  if (!aim) return null;
  const attacker = state.combatants[aim.attackerId];
  if (!attacker) return null;
  const head = titleFor(attacker, aim);
  if (!head) return null;
  if (isCellPick(aim)) {
    const copy = CELL_PICK_COPY[aim.mode];
    const range = aim.mode === "activate_cell" ? weaponRangeForAim(attacker, aim) : null;
    const reach = range ? formatWeaponRange(range).toUpperCase() : copy.reach;
    const sub = [copy.verb, reach, legalCells !== undefined ? cellCountLine(legalCells) : ""].filter(Boolean).join(" · ");
    return { kind: "attack", ...head, sub };
  }
  const kind: TargetingKind = aim.mode === "quick_tech" || aim.mode === "full_tech" || aim.mode === "tech_attack" ? "tech" : "attack";
  const count = legalCount > 0 ? `${legalCount} IN RANGE` : "NOTHING IN RANGE";
  return { kind, ...head, sub: `${kind === "tech" ? "TECH" : "ATTACK"} · ${reachFor({ state, attacker, aim, kind })} · ${count}` };
}

/**
 * **The hovered legal target while an aim is armed** (`token-command-menu` 2c.10): the one seam a
 * targeting-mode hover forecast (accuracy, difficulty, resistance and their sources on the hovered
 * enemy) reads. `null` unless an aim is armed and the pointer is over a unit that aim may legally
 * target (`legalTargetIds`, the same set the board rings). This project builds no forecast.
 */
export type ArmedHoverTarget = { aim: ActionDraft; targetId: string };

export function armedHoverTarget(args: { aim: ActionDraft | null; hoveredId: string | null; legalTargetIds: readonly string[] | null }): ArmedHoverTarget | null {
  const { aim, hoveredId, legalTargetIds } = args;
  if (!aim || !hoveredId || !legalTargetIds?.includes(hoveredId)) return null;
  return { aim, targetId: hoveredId };
}

export function TargetingMode({
  banner,
  onCancel,
  hoverTarget,
  cancel = true,
  onConfirm,
}: {
  banner: TargetingBanner;
  onCancel: () => void;
  /** Whether the banner shows its `CANCEL · ESC`; Esc and a board right-click disarm either way. */
  cancel?: boolean;
  hoverTarget: ArmedHoverTarget | null;
  /** Present, the banner shows a `CONFIRM` that commits a staged pick (a cell prompt's last section). */
  onConfirm?: () => void;
}) {
  const rgb = { "--tcm-tgt-rgb": TARGETING_RGB[banner.kind] } as CSSProperties;
  return (
    <>
      <div className="tcm-tgt-glow" style={rgb} data-kind={banner.kind} aria-hidden="true" />
      <div className="tcm-tgt-dock">
        <div className="tcm-tgt-banner" style={rgb} role="status" data-kind={banner.kind} data-hover-target={hoverTarget?.targetId}>
          <span className="tcm-tgt-stripe" aria-hidden="true" />
          <span className="tcm-tgt-text">
            <span className="tcm-tgt-head">
              <span className="tcm-tgt-title" title={banner.title}>
                <Icon icon={faCrosshairs} className="icon-lead" />
                {banner.title}
              </span>
              {banner.step && <span className="tcm-tgt-step">{banner.step}</span>}
            </span>
            <span className="tcm-tgt-sub">{banner.sub}</span>
          </span>
          {onConfirm && (
            <button type="button" className="tcm-tgt-cancel tcm-tgt-confirm" onClick={onConfirm}>
              CONFIRM
            </button>
          )}
          {cancel && (
            <button type="button" className="tcm-tgt-cancel" onClick={onCancel}>
              CANCEL · ESC
            </button>
          )}
        </div>
      </div>
    </>
  );
}
